/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';

// UI lib dependencies
import { Box, Paper, Group, Image } from '@mantine/core';

// UI local dependencies
import {
	AddFiltersButton,
	Button,
	Pagination,
} from '@components/atoms';
import {
	ConfirmationModal,
	MenuFilter,
} from '@components/molecules';
import { Datatable } from '@components/organisms';

// Styles
import classes from './users.module.css';

// Hooks
import { useUsers } from '@features/resources/users/hooks/useUsers';

// Icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Datatable config
import { tableSchemaBase as columns } from '../../../../../features/resources/users/users.columns';

// Utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';
import { TTargetColumn } from '@global-types/index';

// Apis
import { useUsersApi } from '@features/resources/users/api/get-users';
import { useAllOrganizations } from 'shared/api/get-all-organizations';

// Constants
import { ROLES_OPTIONS } from '@utils/options';
import { DrawerUserShowMore } from '@features/resources/users/components/DrawerUserShowMore';
import { useRemove } from '@features/resources/users/api/remove-user';
import { AddUser } from '@features/resources/users/components/AddUser';
import { UpdateUser } from '@features/resources/users/components/UpdateUser';
import { queryClient } from '@lib/react-query';
import { useEnableUser } from '@features/resources/users/api/enable-user';
import { useDisableUser } from '@features/resources/users/api/disable-user';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Users = () => {
	/* ******************************** HOOKS ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { t } = useTranslation();

	const {
		containerRef,
		usersPage,
		usersPageSize,
		handleScroll,
		setResetFilterUsers,
		isOverflowFilters,
		showScrollFilters,
		ref,
		handlerowviewmore,
		handlerowremoveitem,
		handlechangesortstatus,
		handlePaginationChangeSize,
		handleChangePagination,
		openDrawer,
		handledeleteconversionfactor,
		resetFilterProjects,
		setResetFilterProjects,
		usersSortStatus,
		usersSelectedRow,
		handleCloseDrawerShowMoreUser,
		usersSortParams,
		usersQueryParams,
		usersOpenConfirmationModalDelete,
		handleclosedeleteconfirmationmodal,
		handledeleteconfirmationmodalconfirm,
		handleselectedrecordchange,
		handlerowclicked,
		usersDatatableOptions,
		setUsersDatatableOptions,
		handleedituser,
		openEditForm,
		handlecloseedituser,
		setOpenEditForm,
		setOpenDrawer,
		handleapplyorganizationfilter,
		handleapplyrolefilter,
		openDrawerAddUser,
		setOpenDrawerAddUser,
		setUsersSelectedRow,
	} = useUsers();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useUsersApi(
		usersPage,
		usersPageSize,
		usersQueryParams,
		usersSortParams
	);

	const remove = useRemove(usersPage, usersPageSize);

	const { data: organizations } = useAllOrganizations();

	const disableUser = useDisableUser({
		mutationConfig: {
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: [
						'users',
						{ page: usersPage, size: usersPageSize },
					],
				});
				setUsersSelectedRow({
					...usersSelectedRow,
					disabled: !usersSelectedRow.disabled,
				});
			},
		},
	});

	const enableUser = useEnableUser({
		mutationConfig: {
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: [
						'users',
						{ page: usersPage, size: usersPageSize },
					],
				});
				setUsersSelectedRow({
					...usersSelectedRow,
					disabled: !usersSelectedRow.disabled,
				});
			},
		},
	});

	const { checkAcl, checkAccess } = useAuthorization();

	/* ******************************** RENDERING *************************** */
	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Drawer Edit site ****************************/}
			<UpdateUser
				opened={openEditForm}
				onClose={handlecloseedituser}
			/>

			{/*Drawer add  user */}
			<AddUser
				opened={openDrawerAddUser}
				onClose={() => {
					setOpenDrawerAddUser(false);
				}}
			/>
			{/**************************** Delete confirmation modal ****************************/}

			<ConfirmationModal
				title={t('users.modal.delete.title')}
				description={t('users.modal.delete.description')}
				onConfirm={() =>
					handledeleteconfirmationmodalconfirm(remove)
				}
				onClose={handleclosedeleteconfirmationmodal}
				opened={
					usersOpenConfirmationModalDelete &&
					usersOpenConfirmationModalDelete.open
				}
			/>

			{/**************************** Drawer show more user ****************************/}
			<DrawerUserShowMore
				opened={openDrawer}
				handledelete={handledeleteconversionfactor}
				handleupdate={() => {
					setOpenEditForm(true);
					setOpenDrawer(false);
				}}
				onClose={handleCloseDrawerShowMoreUser}
				position={'right'}
				data={usersSelectedRow}
			/>
			{/**************************** Content ****************************/}
			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={
													handleapplyorganizationfilter
												}
												label={t('global.organization')}
												size="lg"
												options={convertArrayToLabelValue(
													organizations
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterProjects}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
												setResetFilterUsers(_isReset);
											}}
											onApply={handleapplyrolefilter}
											label={t('global.roles')}
											size="lg"
											options={ROLES_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchRole'
											)}
											placeholder={t(
												'filters.placeholders.selectRole'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.USERS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() => {
										setOpenDrawerAddUser(true);
									}}
								>
									{t('users.addUser')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />

				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						onChangeUserStatus={(row: any) => {
							if (!row.disabled) {
								disableUser.mutate(row.id);
							} else {
								enableUser.mutate(row.id);
							}
						}}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setUsersDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={usersDatatableOptions}
						data={data}
						onViewMore={handlerowviewmore}
						onRowClick={handlerowclicked}
						onRemoveItem={handlerowremoveitem}
						onEditItem={handleedituser}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={usersSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={usersPageSize}
					totalitems={totalItems}
					activepage={usersPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
					maxpagesize={1000}
				/>
			</Box>
		</Box>
	);
};

export default Users;
